import { useCallback, useRef, useState } from 'react'
import { useStickyPin, useStickyEscape } from './stickyPin'

/**
 * Hero scroll-detach (#934): React port of the toolkit module's initSticky()
 * hero branch (searchbar-pro.js:4657-4910). A sentinel above the bar drives an
 * IntersectionObserver: once it scrolls above the viewport top the shell pins
 * to a viewport-centered compressed pill; clicking the pill expands it to a
 * top-attached panel with a dimming backdrop; Esc / backdrop click collapses;
 * scrolling back to the top un-pins via the same sentinel. A spacer holds the
 * vacated flow height so the page doesn't jump on pin.
 *
 * Desktop only, matching the module's mqMobile bail (searchbar-pro.js:4661-4663)
 * at the SAME 932px boundary the bar itself swaps to the mobile trigger.
 * Unlike the module (which checks once at init), the media query is observed:
 * a desktop-size restore after a narrow start still gets sticky, and narrowing
 * mid-scroll tears the pinned state down instead of stranding a fixed pill
 * over the mobile trigger. The arming machinery (media gate, sentinel
 * observer, queued-callback guard) lives in the shared scaffold (#1104,
 * stickyPin.ts); this hook owns what the pinned state MEANS for the hero.
 *
 * The compressed summary is NOT built here: the module scrapes the DOM
 * (buildSummary), but the widget's field state is already React state — the
 * caller derives the label from useSearchSummaries().
 *
 * `topOffsetPx` (search.heroStickyTopPx) moves the pin boundary below a host
 * site's fixed header so the bar never slides under it before pinning
 * (#1220 item 3 — filed against the refine bar; the hero shares the defect
 * class and gets the fix through the same scaffold parameter).
 */
export function useHeroSticky(enabled: boolean, topOffsetPx = 0) {
  const shellRef = useRef<HTMLDivElement>(null)
  const [isSticky, setIsSticky] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [spacerHeight, setSpacerHeight] = useState(0)

  const enterSticky = useCallback(() => {
    // Measure BEFORE the pin class applies (this render's DOM is still
    // in-flow), same ordering as the module's enterSticky.
    const shell = shellRef.current
    if (shell && shell.offsetHeight) setSpacerHeight(shell.offsetHeight)
    setIsSticky(true)
  }, [])

  const exitSticky = useCallback(() => {
    setIsSticky(false)
    setIsExpanded(false)
    setSpacerHeight(0)
  }, [])

  const expand = useCallback(() => setIsExpanded(true), [])
  const collapse = useCallback(() => setIsExpanded(false), [])

  const sentinelRef = useRef<HTMLDivElement>(null)
  const armedRef = useRef(false)
  useStickyPin(enabled, sentinelRef, armedRef, {
    onEnter: enterSticky,
    onExit: exitSticky,
    pinBoundaryPx: topOffsetPx,
  })

  // Esc collapses the expanded panel (module: document-level keydown).
  useStickyEscape(isSticky && isExpanded, collapse)

  return { sentinelRef, shellRef, isSticky, isExpanded, spacerHeight, expand, collapse }
}
